import React, { useState, useEffect } from 'react';
import AdminBaseLayout from '../../components/layouts/AdminBaseLayout';
import {
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Snackbar,
  Alert,
  Tooltip,
  InputAdornment,
  IconButton
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import HistoryIcon from '@mui/icons-material/History';
import withAuthAdmin from '../../middleware/useWithAuthAdmin';
import BalanceHttpService from '../../api/BalanceHttpService';
import LoadingOverlay from '../../components/LoadingOverlay';

function AdminBalance() {
  const [balances, setBalances] = useState([]);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [yieldDialog, setYieldDialog] = useState({
    open: false,
    percentage: '',
    date: new Date().toISOString().split('T')[0]
  });
  const [uploadDialog, setUploadDialog] = useState({
    open: false,
    file: null
  });
  const [historyDialog, setHistoryDialog] = useState({
    open: false,
    userId: null,
    history: [],
    page: 0,
    rowsPerPage: 5,
    totalItems: 0
  });
  const [logYieldDialog, setLogYieldDialog] = useState({
    open: false,
    logId: null,
    percentage: ''
  });

  const fetchBalances = async (pageNumber = 1, search) => {
    try {
      setLoading(true);
      const response = await BalanceHttpService.getBalances(pageNumber, rowsPerPage, search);
      setBalances(response.data);
      setTotalItems(response.pagination.total);
      setTotalPages(response.pagination.total_pages);
    } catch (err) {
      showMessage('Erro ao carregar os dados de saldo', 'error');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBalances(page + 1, search);
  }, [page, rowsPerPage, search]);

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(0);
  };

  const handleOpenYieldDialog = () => {
    setYieldDialog({
      open: true,
      percentage: '',
      date: new Date().toISOString().split('T')[0]
    });
  };

  const handleCloseYieldDialog = () => {
    setYieldDialog({
      open: false,
      percentage: '',
      date: new Date().toISOString().split('T')[0]
    });
  };

  const handleApplyYield = async () => {
    try {
      setLoading(true);
      const percentage = parseFloat(yieldDialog.percentage.replace(',', '.'));
      if (percentage < 0 || percentage > 100) {
        showMessage('A porcentagem deve estar entre 0 e 100%', 'error');
        return;
      }
      await BalanceHttpService.applyYield(percentage, yieldDialog.date);
      showMessage('Rendimento aplicado com sucesso!');
      handleCloseYieldDialog();
      fetchBalances(page + 1);
    } catch (err) {
      showMessage('Erro ao aplicar o rendimento', 'error');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const showMessage = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleOpenUploadDialog = () => {
    setUploadDialog({
      open: true,
      file: null
    });
  };

  const handleCloseUploadDialog = () => {
    setUploadDialog({
      open: false,
      file: null
    });
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type !== 'text/csv') {
      showMessage('Por favor, selecione um arquivo CSV válido', 'error');
      event.target.value = null;
      return;
    }
    setUploadDialog(prev => ({
      ...prev,
      file
    }));
  };

  const downloadSampleCSV = () => {
    const csvContent = 'cpf;valor;cnpj;origem\n12345678912;150.50;12345678000199;alloyal\n98765432100;75.25;98765432000199;alloyal';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'modelo_cashback.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleUpload = async () => {
    if (!uploadDialog.file) {
      showMessage('Por favor, selecione um arquivo CSV', 'error');
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('csv_file', uploadDialog.file);
      await BalanceHttpService.uploadCashbackCSV(formData);
      showMessage('Arquivo processado com sucesso!');
      handleCloseUploadDialog();
      fetchBalances(page + 1);
    } catch (error) {
      showMessage(error.message || 'Erro ao processar arquivo', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenHistoryDialog = async (userId) => {
    try {
      setLoading(true);
      const response = await BalanceHttpService.getUserBalanceHistory(userId, 1, historyDialog.rowsPerPage);
      setHistoryDialog({
        open: true,
        userId,
        history: response.data,
        page: 0,
        rowsPerPage: 5,
        totalItems: response.pagination.total
      });
    } catch (error) {
      showMessage('Erro ao carregar o histórico', 'error');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseHistoryDialog = () => {
    setHistoryDialog({
      open: false,
      userId: null,
      history: [],
      page: 0,
      rowsPerPage: 5,
      totalItems: 0
    });
  };

  const handleHistoryPageChange = async (event, newPage) => {
    try {
      setLoading(true);
      const response = await BalanceHttpService.getUserBalanceHistory(
        historyDialog.userId,
        newPage + 1,
        historyDialog.rowsPerPage
      );
      setHistoryDialog(prev => ({
        ...prev,
        history: response.data,
        page: newPage,
        totalItems: response.pagination.total
      }));
    } catch (error) {
      showMessage('Erro ao carregar o histórico', 'error');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleHistoryRowsPerPageChange = async (event) => {
    const newRowsPerPage = parseInt(event.target.value, 5);
    try {
      setLoading(true);
      const response = await BalanceHttpService.getUserBalanceHistory(
        historyDialog.userId,
        1,
        newRowsPerPage
      );
      setHistoryDialog(prev => ({
        ...prev,
        history: response.data,
        page: 0,
        rowsPerPage: newRowsPerPage,
        totalItems: response.pagination.total
      }));
    } catch (error) {
      showMessage('Erro ao carregar o histórico', 'error');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenLogYieldDialog = (logId) => {
    setLogYieldDialog({
      open: true,
      logId,
      percentage: ''
    });
  };

  const handleCloseLogYieldDialog = () => {
    setLogYieldDialog({
      open: false,
      logId: null,
      percentage: ''
    });
  };

  const handlePercentageChange = (e) => {
    let value = e.target.value;
    
    // Remove tudo exceto números e vírgula
    value = value.replace(/[^\d,]/g, '');
    
    // Se começar com vírgula, adiciona 0 antes
    if (value.startsWith(',')) {
      value = '0' + value;
    }
    
    // Garante apenas uma vírgula
    const parts = value.split(',');
    if (parts.length > 2) {
      value = parts[0] + ',' + parts.slice(1).join('');
    }
    
    // Formata a parte inteira com no máximo 3 dígitos
    if (parts[0].length > 3) {
      parts[0] = parts[0].slice(0, 3);
    }
    
    // Limita a 3 casas decimais
    if (parts[1] && parts[1].length > 3) {
      parts[1] = parts[1].slice(0, 3);
    }

    // Reconstrói o valor com as partes formatadas
    value = parts[0] + (parts.length > 1 ? ',' + parts[1] : '');

    // Se o valor for maior que 100, limita a 100
    const numValue = parseFloat(value.replace(',', '.'));
    if (numValue > 100) {
      value = '100,000';
    }

    setLogYieldDialog(prev => ({ ...prev, percentage: value }));
  };

  const handleApplyLogYield = async () => {
    try {
      setLoading(true);
      const percentage = parseFloat(logYieldDialog.percentage.replace(',', '.'));
      if (percentage < 0 || percentage > 100) {
        showMessage('A porcentagem deve estar entre 0 e 100%', 'error');
        return;
      }

      // Aplica o rendimento
      await BalanceHttpService.applyLogCashbackYield(logYieldDialog.logId, percentage);
      
      // Atualiza o histórico imediatamente
      if (historyDialog.userId) {
        try {
          const response = await BalanceHttpService.getUserBalanceHistory(historyDialog.userId);
          setHistoryDialog(prev => ({
            ...prev,
            history: response.data
          }));
        } catch (error) {
          console.error('Erro ao atualizar histórico:', error);
          // Mesmo com erro no histórico, mostra mensagem de sucesso do rendimento
        }
      }
      
      // Atualiza a tabela de saldos
      await fetchBalances(page + 1);
      
      showMessage('Rendimento aplicado com sucesso!');
      handleCloseLogYieldDialog();
    } catch (err) {
      showMessage('Erro ao aplicar o rendimento', 'error');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatValue = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  return (
    <AdminBaseLayout>
      <LoadingOverlay open={loading} />
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4">
            Saldos dos Usuários
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Tooltip title="Adicionar Rendimento">
              <Button
                variant="contained"
                color="primary"
                startIcon={<TrendingUpIcon />}
                onClick={handleOpenYieldDialog}
              >
                Adicionar Rendimento
              </Button>
            </Tooltip>
            <Tooltip title="Adicionar Cashback em Massa">
              <Button
                variant="contained"
                color="primary"
                startIcon={<CloudUploadIcon />}
                onClick={handleOpenUploadDialog}
                sx={{
                  backgroundColor: "#FF6B00",
                  '&:hover': {
                    backgroundColor: "#e65100",
                  }
                }}
              >
                Adicionar Cashback em Massa
              </Button>
            </Tooltip>
          </Box>
        </Box>

        <Box sx={{ mb: 1, px: { xs: 1, sm: 0 } }}>
          <TextField
              fullWidth
              label="Buscar usuario"
              variant="outlined"
              value={search}
              onChange={handleSearchChange}
              size="small"
              sx={{
                '& .MuiInputLabel-root': {
                  fontSize: { xs: '0.8rem', sm: '1rem' }
                },
                '& .MuiInputBase-input': {
                  fontSize: { xs: '0.8rem', sm: '1rem' },
                  padding: { xs: '8px', sm: '14px' }
                }
              }}
          />
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>CPF</TableCell>
                <TableCell>Email</TableCell>
                <TableCell align="right">Saldo</TableCell>
                <TableCell align="center">Histórico</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {balances.map((balance) => (
                <TableRow key={balance.id}>
                  <TableCell>{balance.name}</TableCell>
                  <TableCell>{balance.cpf}</TableCell>
                  <TableCell>{balance.email}</TableCell>
                  <TableCell align="right">
                    {formatValue(balance.balance)}
                  </TableCell>

                  <TableCell align="center">
                    <Tooltip title="Ver Histórico">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<HistoryIcon />}
                        onClick={() => handleOpenHistoryDialog(balance.user_id)}
                      >
                        Histórico
                      </Button>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 20, 30, 50]}
            component="div"
            count={totalItems}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Linhas por página"
          />
        </TableContainer>

        {/* Modal de rendimento */}
        <Dialog open={yieldDialog.open} onClose={handleCloseYieldDialog}>
          <DialogTitle>Adicionar Rendimento</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Porcentagem de Rendimento"
              type="text"
              fullWidth
              value={yieldDialog.percentage}
              onChange={(e) => {
                let value = e.target.value;
                
                // Permite campo vazio
                if (value === '') {
                  setYieldDialog({ ...yieldDialog, percentage: '' });
                  return;
                }

                // Remove qualquer caractere que não seja número ou vírgula
                value = value.replace(/[^\d,]/g, '');
                
                // Garante que só tenha uma vírgula decimal
                const parts = value.split(',');
                if (parts.length > 2) {
                  value = parts[0] + ',' + parts.slice(1).join('');
                }
                
                // Limita a duas casas decimais
                if (parts[1]?.length > 2) {
                  const numValue = parseFloat(value.replace(',', '.'));
                  value = numValue.toFixed(2).replace('.', ',');
                }

                // Verifica se o valor é menor ou igual a 100
                const numValue = parseFloat(value.replace(',', '.'));
                if (!isNaN(numValue) && numValue <= 100) {
                  setYieldDialog({ ...yieldDialog, percentage: value });
                }
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              inputProps={{ 
                step: "0.01",
                min: "0",
                max: "100"
              }}
              helperText="Digite a porcentagem de rendimento entre 0 e 100% (ex: 2,5 para 2,5%)"
            />
            <TextField
              margin="dense"
              label="Data"
              type="date"
              fullWidth
              value={yieldDialog.date}
              onChange={(e) => setYieldDialog({ ...yieldDialog, date: e.target.value })}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseYieldDialog}>Cancelar</Button>
            <Button 
              onClick={handleApplyYield} 
              color="primary"
              disabled={!yieldDialog.percentage || parseFloat(yieldDialog.percentage.replace(',', '.')) > 100 || parseFloat(yieldDialog.percentage.replace(',', '.')) < 0}
            >
              Aplicar Rendimento
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal de Upload */}
        <Dialog
          open={uploadDialog.open}
          onClose={handleCloseUploadDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ 
            bgcolor: '#FF6B00', 
            color: 'white',
            fontSize: '1.5rem',
            py: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <CloudUploadIcon sx={{ fontSize: '2rem' }} />
            Adicionar Cashback em Massa
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" color="text.secondary">
                Faça o upload de um arquivo CSV com os campos na seguinte ordem: cpf, valor, cnpj e origem.
              </Typography>
              <Box sx={{ mt: 3, mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Exemplo de arquivo CSV válido:
                </Typography>
                <Box
                  sx={{
                    backgroundColor: '#f5f5f5',
                    p: 2,
                    borderRadius: 1,
                    fontFamily: 'monospace',
                    mb: 2
                  }}
                >
                  cpf,valor,cnpj,origem<br />
                  12345678901,150.50,12345678000199,alloyal<br />
                  98765432109,75.25,98765432000199,alloyal
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<CloudUploadIcon />}
                  sx={{
                    backgroundColor: "#FF6B00",
                    '&:hover': {
                      backgroundColor: "#e65100",
                    },
                  }}
                >
                  Selecionar Arquivo
                  <input
                    type="file"
                    hidden
                    onChange={handleFileChange}
                    accept=".csv"
                  />
                </Button>
                {uploadDialog.file && (
                  <Typography variant="body2" color="text.secondary">
                    Arquivo selecionado: {uploadDialog.file.name}
                  </Typography>
                )}
              </Box>

              <Button
                    onClick={handleCloseUploadDialog}
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={downloadSampleCSV}
                    sx={{ mt: 2 }}
                    size="large"
                  >
                    Baixar Modelo CSV
                  </Button>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button
              onClick={handleCloseUploadDialog}
              variant="outlined"
              size="large"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleUpload}
              variant="contained"
              size="large"
              sx={{
                backgroundColor: "#FF6B00",
                '&:hover': {
                  backgroundColor: "#e65100",
                },
                minWidth: '120px'
              }}
            >
              Enviar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal de Histórico */}
        <Dialog
          open={historyDialog.open}
          onClose={handleCloseHistoryDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ 
            bgcolor: '#FF6B00', 
            color: 'white',
            fontSize: '1.5rem',
            py: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <HistoryIcon sx={{ fontSize: '2rem' }} />
            Histórico de Saldo
          </DialogTitle>
          <DialogContent>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell align="right">Saldo Anterior</TableCell>
                    <TableCell align="right">Valor Adicionado</TableCell>
                    <TableCell align="right">Novo Saldo</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Atualizado Por</TableCell>
                    <TableCell>Origem</TableCell>
                    <TableCell align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyDialog.history.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        Nenhum registro encontrado
                      </TableCell>
                    </TableRow>
                  ) : (
                    historyDialog.history.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          {formatDate(item.date)}
                        </TableCell>
                        <TableCell align="right">
                          {formatValue(item.old_balance)}
                        </TableCell>
                        <TableCell align="right">
                          {formatValue(item.added_value)}
                        </TableCell>
                        <TableCell align="right">
                          {formatValue(item.new_balance)}
                        </TableCell>
                        <TableCell>{item.type}</TableCell>
                        <TableCell>{item.updated_by}</TableCell>
                        <TableCell>{item.origin ?? 'Vai pra conta'}</TableCell>
                        <TableCell align="center">
                        <Tooltip title="Adicionar Rendimento">
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<TrendingUpIcon />}
                            onClick={() => handleOpenLogYieldDialog(item.log_id)}
                          >
                            Rendimento
                          </Button>
                        </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={historyDialog.totalItems}
              page={historyDialog.page}
              onPageChange={handleHistoryPageChange}
              rowsPerPage={historyDialog.rowsPerPage}
              onRowsPerPageChange={handleHistoryRowsPerPageChange}
              rowsPerPageOptions={[10, 20, 50]}
              labelRowsPerPage="Itens por página"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseHistoryDialog}>Fechar</Button>
          </DialogActions>
        </Dialog>

        {/* Modal de Rendimento */}
        <Dialog
          open={yieldDialog.open}
          onClose={handleCloseYieldDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{
            bgcolor: '#FF6B00',
            color: 'white',
            fontSize: '1.5rem',
            py: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <TrendingUpIcon sx={{ fontSize: '2rem' }} />
            Adicionar Rendimento
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Box sx={{ mt: 2 }}>
              <TextField
                  autoFocus
                  margin="dense"
                  label="Porcentagem de Rendimento"
                  type="text"
                  fullWidth
                  value={yieldDialog.percentage}
                  onChange={(e) => {
                    let value = e.target.value;

                    // Permite campo vazio
                    if (value === '') {
                      setYieldDialog({ ...yieldDialog, percentage: '' });
                      return;
                    }

                    // Remove qualquer caractere que não seja número ou vírgula
                    value = value.replace(/[^\d,]/g, '');

                    // Garante que só tenha uma vírgula decimal
                    const parts = value.split(',');
                    if (parts.length > 2) {
                      value = parts[0] + ',' + parts.slice(1).join('');
                    }

                    // Limita a duas casas decimais
                    if (parts[1]?.length > 2) {
                      const numValue = parseFloat(value.replace(',', '.'));
                      value = numValue.toFixed(2).replace('.', ',');
                    }

                    // Verifica se o valor é menor ou igual a 100
                    const numValue = parseFloat(value.replace(',', '.'));
                    if (!isNaN(numValue) && numValue <= 100) {
                      setYieldDialog({ ...yieldDialog, percentage: value });
                    }
                  }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  inputProps={{
                    step: "0.01",
                    min: "0",
                    max: "100"
                  }}
                  helperText="Digite a porcentagem de rendimento entre 0 e 100% (ex: 2,5 para 2,5%)"
              />
              <TextField
                  margin="dense"
                  label="Data"
                  type="date"
                  fullWidth
                  value={yieldDialog.date}
                  onChange={(e) => setYieldDialog({ ...yieldDialog, date: e.target.value })}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button
              onClick={handleCloseYieldDialog}
              variant="outlined"
              size="large"
            >
              Cancelar
            </Button>
            <Button
                onClick={handleApplyYield}
                color="primary"
                disabled={!yieldDialog.percentage || parseFloat(yieldDialog.percentage.replace(',', '.')) > 100 || parseFloat(yieldDialog.percentage.replace(',', '.')) < 0}
            >
              Aplicar Rendimento
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal de Rendimento para Log específico */}
        <Dialog
          open={logYieldDialog.open}
          onClose={handleCloseLogYieldDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ 
            bgcolor: '#FF6B00', 
            color: 'white',
            fontSize: '1.5rem',
            py: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <TrendingUpIcon sx={{ fontSize: '2rem' }} />
            Adicionar Rendimento ao Cashback
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Box sx={{ mt: 2 }}>
              <TextField
                autoFocus
                label="Porcentagem do Rendimento"
                type="text"
                fullWidth
                value={logYieldDialog.percentage}
                onChange={handlePercentageChange}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    fontSize: '1.1rem',
                  }
                }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Digite a porcentagem de rendimento que será aplicada a este cashback específico.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button
              onClick={handleCloseLogYieldDialog}
              variant="outlined"
              size="large"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleApplyLogYield}
              variant="contained"
              size="large"
              sx={{
                backgroundColor: "#FF6B00",
                '&:hover': {
                  backgroundColor: "#e65100",
                },
                minWidth: '120px'
              }}
            >
              Aplicar
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar 
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{ mt: 7 }}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </AdminBaseLayout>
  );
}

export default withAuthAdmin(AdminBalance);
